/**
 * Shared chat panel. Used in the lobby (as an inline card) and in-game
 * (inside a floating overlay). Auto-scrolls to the newest message, sends
 * on Enter, and visually distinguishes the local user's messages.
 */

import { useEffect, useRef, useState } from "react";
import type { ChatMessage } from "../lib/useLobbyWs.js";

/** Server caps inbound chat at 240 chars; mirror that here so users see the limit. */
const CHAT_MAX_LEN = 240;

export interface ChatPanelProps {
  messages: ChatMessage[];
  myId: string | null;
  onSend: (text: string) => void;
  /** Override the messages-list height. Defaults to 240px (lobby view). */
  height?: number;
  /** Auto-focus the input when the panel mounts. */
  autoFocus?: boolean;
}

export function ChatPanel({
  messages,
  myId,
  onSend,
  height = 240,
  autoFocus = false,
}: ChatPanelProps) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the newest message whenever the list grows.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  function submit() {
    const text = draft.trim();
    if (!text) return;
    onSend(text.slice(0, CHAT_MAX_LEN));
    setDraft("");
  }

  return (
    <div className="chat-panel">
      <div className="chat-messages" ref={listRef} style={{ height }}>
        {messages.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, padding: 12, textAlign: "center" }}>
            No messages yet — say hi to your table.
          </div>
        ) : (
          messages.map((m, i) => {
            const mine = myId !== null && m.from === myId;
            return (
              <div key={`${m.at}-${i}`} className={`chat-row ${mine ? "mine" : ""}`}>
                <div className="chat-meta">
                  <span className="chat-from">{mine ? "You" : m.fromName}</span>
                  <span className="chat-time">{formatRelTime(m.at)}</span>
                </div>
                <div className="chat-text">{m.text}</div>
              </div>
            );
          })
        )}
      </div>
      <div className="chat-input-row">
        <input
          className="chat-input"
          placeholder="Type a message…"
          maxLength={CHAT_MAX_LEN}
          autoFocus={autoFocus}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button className="primary" disabled={!draft.trim()} onClick={submit}>
          Send
        </button>
      </div>
    </div>
  );
}

/** "now", "12s", "3m", "1h"; falls back to clock time for >24h. */
function formatRelTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 5_000) return "now";
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
